import {
  ArrowLeftRight,
  BellRing,
  BookOpen,
  Boxes,
  CalendarSearch,
  Coins,
  Eye,
  FileCheck,
  FileSignature,
  FileText,
  KeyRound,
  ListChecks,
  ShieldCheck,
  Timer,
  Upload,
  type LucideIcon,
} from "lucide-react";

export const ROTATION_MS = 15000;

export type CategoryAccent = "primary" | "secondary" | "accent";

export type CategoryFeature = {
  icon: LucideIcon;
  labelKey: string;
  video?: string;
  images?: string[];
  duration?: number;
};

export type Category = {
  id: string;
  icon: LucideIcon;
  accent: CategoryAccent;
  labelKey: string;
  titleKey: string;
  descriptionKey: string;
  features: CategoryFeature[];
};

export const categories: Category[] = [
  {
    id: "clarte",
    icon: Eye,
    accent: "primary",
    labelKey: "features.categories.clarte.label",
    titleKey: "features.categories.clarte.title",
    descriptionKey: "features.categories.clarte.description",
    features: [
      {
        icon: CalendarSearch,
        labelKey: "features.categories.clarte.features.booking",
        video: "/assets/videos/booking.mp4",
      },
      {
        icon: Boxes,
        labelKey: "features.categories.clarte.features.inventory",
        video: "/assets/videos/inventory.mp4",
      },
      {
        icon: Coins,
        labelKey: "features.categories.clarte.features.pricing",
        video: "/assets/videos/pricing.mp4",
      },
      {
        icon: ArrowLeftRight,
        labelKey: "features.categories.clarte.features.overview",
        images: ["/assets/images/overview.png"],
      },
    ],
  },
  {
    id: "temps-gagne",
    icon: Timer,
    accent: "secondary",
    labelKey: "features.categories.tempsGagne.label",
    titleKey: "features.categories.tempsGagne.title",
    descriptionKey: "features.categories.tempsGagne.description",
    features: [
      {
        icon: Upload,
        labelKey: "features.categories.tempsGagne.features.import",
        video: "/assets/videos/import.mp4",
      },
      {
        icon: BellRing,
        labelKey: "features.categories.tempsGagne.features.alerts",
        video: "/assets/videos/alerts.mp4",
      },
      {
        icon: FileText,
        labelKey: "features.categories.tempsGagne.features.quotes",
        video: "https://cdn.coverr.co/videos/coverr-writing-on-paper-1572/1080p.mp4",
      },
      {
        icon: BookOpen,
        labelKey: "features.categories.tempsGagne.features.logbook",
        video: "https://cdn.coverr.co/videos/coverr-reading-a-book-1572/1080p.mp4",
      },
    ],
  },
  {
    id: "securisation",
    icon: ShieldCheck,
    accent: "accent",
    labelKey: "features.categories.securisation.label",
    titleKey: "features.categories.securisation.title",
    descriptionKey: "features.categories.securisation.description",
    features: [
      {
        icon: FileSignature,
        labelKey: "features.categories.securisation.features.contracts",
        video: "/assets/videos/signature.mp4",
        duration: 27000,
      },
      {
        icon: KeyRound,
        labelKey: "features.categories.securisation.features.twoFactor",
        video: "/assets/videos/2FA.mp4",
        duration: 17000,
      },
      {
        icon: FileCheck,
        labelKey: "features.categories.securisation.features.insurance",
        video: "https://cdn.coverr.co/videos/coverr-writing-on-paper-1572/1080p.mp4",
      },
      {
        icon: ListChecks,
        labelKey: "features.categories.securisation.features.checklists",
        video: "https://cdn.coverr.co/videos/coverr-checking-a-list-1572/1080p.mp4",
      },
    ],
  },
];

export const accentBg: Record<CategoryAccent, string> = {
  primary: "bg-primary text-primary-foreground",
  secondary: "bg-secondary text-secondary-foreground",
  accent: "bg-accent text-accent-foreground",
};

export const accentSoft: Record<CategoryAccent, string> = {
  primary: "bg-primary/15 text-foreground",
  secondary: "bg-secondary/15 text-foreground",
  accent: "bg-accent/15 text-foreground",
};

export const accentRing: Record<CategoryAccent, string> = {
  primary: "ring-primary/30",
  secondary: "ring-secondary/30",
  accent: "ring-accent/30",
};

export const accentProgress: Record<CategoryAccent, string> = {
  primary: "bg-primary",
  secondary: "bg-secondary",
  accent: "bg-accent",
};

export const accentActiveBorder: Record<CategoryAccent, string> = {
  primary: "border-primary",
  secondary: "border-secondary",
  accent: "border-accent",
};
